import { z } from 'zod'

export const DocTypeSchema = z.enum(['pdf', 'docx', 'md', 'txt', 'html'])
export type DocType = z.infer<typeof DocTypeSchema>

export const DocumentPhaseSchema = z.enum(['uploading', 'reading', 'indexing', 'ready', 'failed'])
export type DocumentPhase = z.infer<typeof DocumentPhaseSchema>

export type UploadJobStatus =
  | { phase: 'uploading'; progressPercent: number }
  | { phase: 'reading' }
  | { phase: 'indexing' }
  | { phase: 'ready' }
  | { phase: 'failed'; errorMessage?: string }

export type UploadJob = {
  id: string
  documentId: string
  displayName: string
  filename: string
  fileSizeBytes?: number
  status: UploadJobStatus
  isDismissed: boolean
}

export const UPLOAD_ALLOWED_EXTS = DocTypeSchema.options
export const UPLOAD_MAX_BYTES = 5 * 1024 * 1024

// Shown on the upload card when processing fails without a specific backend
// failure_reason — e.g. the document's events stream errors out (5xx) after the
// upload itself succeeded. Phase-agnostic on purpose: the stream can give up
// during either the reading or indexing phase. Structured failure events keep
// their own `detail`.
export const PROCESSING_FAILED_MESSAGE =
  'Something went wrong while processing this document. Please try again, and contact Support if the issue continues.'

export const formatFileSize = (bytes: number): string => {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(bytes >= 10 * 1024 * 1024 ? 0 : 1)} MB`
  }
  if (bytes >= 1024) {
    return `${Math.round(bytes / 1024)} KB`
  }
  return `${bytes} B`
}

export const DOC_TYPE_META: Record<DocType, { label: string; mimeTypes: string[] }> = {
  pdf: { label: 'PDF', mimeTypes: ['application/pdf'] },
  docx: {
    label: 'DOCX',
    mimeTypes: ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
  },
  md: { label: 'Markdown', mimeTypes: ['text/markdown', 'text/x-markdown'] },
  txt: { label: 'Text', mimeTypes: ['text/plain'] },
  html: { label: 'HTML', mimeTypes: ['text/html'] },
}

export const getDocType = (filename: string): DocType | undefined => {
  const parsed = DocTypeSchema.safeParse(filename.split('.').pop()?.toLowerCase())
  return parsed.success ? parsed.data : undefined
}

export type DocTypeBadgeClasses = Record<DocType, string> & { base: string }

export const getDocTypeBadgeClass = ({
  docType,
  classes,
}: {
  docType: DocType | undefined
  classes: DocTypeBadgeClasses
}): string => (docType ? classes[docType] : classes.base)

export const PHASE_PROGRESS: Record<Exclude<DocumentPhase, 'uploading' | 'failed'>, number> = {
  reading: 65,
  indexing: 92,
  ready: 100,
}
